if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const variantId = Number(formData.get('id'));
        const quantity = parseInt(formData.get('quantity')) || 1;
        const upsellVariantId = this.getSelectedUpsellVariantId();

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (upsellVariantId) {
          config.headers['Content-Type'] = 'application/json';

          // Add parent first. Nesting via parent_id in the same request is unreliable
          // when the parent already exists or merges — child can land as a sibling.
          // Child is attached afterwards with parent_line_key.
          const payload = {
            items: [
              {
                id: variantId,
                quantity,
              },
            ],
          };

          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
            payload.sections_url = window.location.pathname;
          }

          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }

          config.body = formData;
        }

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            const sectionIds = this.cart
              ? this.cart.getSectionsToRender().map((section) => section.id).join(',')
              : '';

            const refreshCartSections = async (cartResponse) => {
              let next = cartResponse || {};
              if (this.cart && typeof UpsellUtils !== 'undefined') {
                const hydrated = await UpsellUtils.hydrateSections(next, sectionIds);
                if (hydrated?.sections) next = hydrated;
              }
              return next;
            };

            const paintCart = async (cartResponse, { openCart = true } = {}) => {
              let next = await refreshCartSections(cartResponse);

              if (upsellVariantId && this.cart && typeof window.syncAllNestedUpsellQuantities === 'function') {
                const syncedState = await window.syncAllNestedUpsellQuantities({
                  sections: sectionIds,
                  sectionsUrl: window.location.pathname,
                });
                if (syncedState) {
                  next = { ...next, ...syncedState, sections: syncedState.sections || next.sections };
                }
              }

              if (!next.sections) {
                next = await refreshCartSections(next);
              }

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: next,
              });

              if (openCart && this.cart) {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(next);
                });
              }

              return next;
            };

            const errorMessage =
              response.description ||
              response.message ||
              (typeof response.errors === 'string' ? response.errors : '');
            const hasSoftInventoryMessage = /added to your cart|availability|inventory|not enough/i.test(
              errorMessage || ''
            );

            // Shopify may return status/422 for partial inventory adds while still
            // mutating the cart. Refresh UI in that case instead of bailing out.
            if (response.status && !hasSoftInventoryMessage && !response.items && response.item_count == null) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(errorMessage);
              this.dispatchCartErrorEvent(errorMessage, 'INVALID');
              linesUpdateDeferred?.reject(new Error(errorMessage));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (response.status && hasSoftInventoryMessage) {
              this.handleErrorMessage(errorMessage);
            }

            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            if (upsellVariantId && typeof UpsellUtils !== 'undefined') {
              // Nest only as many inserts as remaining stock allows.
              let nestQuantity = quantity;
              const config = window.variantUpsellConfig?.[String(variantId)];
              const resolved = config
                ? UpsellUtils.resolveUpsellForParent(config.parentTitle, config.recommendations)
                : null;

              if (resolved && typeof UpsellUtils.refreshCartQuantities === 'function') {
                await UpsellUtils.refreshCartQuantities();
                if (resolved.inventory_management && String(resolved.inventory_policy || '').toLowerCase() !== 'continue') {
                  const inCart = Number(window.ProductUpsellPickerHelpers?.getCartQuantity?.(resolved.id) || 0);
                  const stock = Number(resolved.inventory_quantity);
                  const remaining = Number.isFinite(stock) ? stock - inCart : nestQuantity;
                  nestQuantity = Math.max(0, Math.min(nestQuantity, remaining));
                }
              }

              if (nestQuantity > 0) {
                const ensured = await UpsellUtils.ensureNestedUpsellAdded({
                  variantId,
                  upsellVariantId,
                  quantity: nestQuantity,
                  sections: sectionIds,
                  sectionsUrl: window.location.pathname,
                });

                if (ensured?.status) {
                  this.handleErrorMessage(
                    ensured.description || ensured.message || errorMessage || 'Unable to add insert.'
                  );
                } else if (ensured?.sections) {
                  response = { ...response, sections: ensured.sections };
                }
              } else if (!errorMessage) {
                this.handleErrorMessage('Insert is out of stock.');
              }
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    paintCart(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              await paintCart(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getSelectedUpsellVariantId() {
        const sectionId = this.dataset.sectionId;
        const picker =
          document.querySelector(`product-upsell-picker[data-section-id="${sectionId}"]`) ||
          document.querySelector('product-upsell-picker');
        if (!picker) return null;

        const checked = picker.querySelector('input[type="radio"]:checked');
        const value = checked?.value;
        if (!value) return null;

        const currentVariantId = Number(this.variantIdInput?.value);
        const config = window.variantUpsellConfig?.[String(currentVariantId)];
        if (config && typeof UpsellUtils !== 'undefined') {
          const resolved = UpsellUtils.resolveUpsellForParent(config.parentTitle, config.recommendations);
          if (resolved?.id && resolved.available !== false) return Number(resolved.id);
          return null;
        }

        if (typeof picker.selectedVariantId === 'number') return picker.selectedVariantId;

        return Number(value);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
